import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Store, createSelector } from '@ngrx/store';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import { MenuItem } from 'primeng/api';
import Quill, { Delta } from 'quill/core';

import { BehaviorSubject, combineLatest, map, switchMap } from 'rxjs';

import { QuillTemplateVariableToolbarDirective } from 'src/app/shared/quill-template-variable.directive';
import { generateUUID } from 'src/app/utilities/state.util';
import { SubSink } from 'subsink';

import { BaseCommentModeFragment, BaseJobUserFragment, BaseUserFragment, FullJobFragment, QuillTemplateLocation } from '../../../../generated/graphql.generated';
import { ArtifactService, PlusAuthenticationService } from '../../../core/public-api';
import { FreyaCommonModule } from '../../../freya-common/freya-common.module';
import { FreyaNotificationsService } from '../../../services/freya-notifications.service';
import { PermissionService } from '../../../services/permission.service';
import { appropriateTimeMeasurement } from '../../../shared/appropriate-time-measurement.pipe';
import { COMMENT_MODES_TEMPLATES } from '../../../shared/quill-modules/quill-templates.helpers';
import { SharedModule } from '../../../shared/shared.module';
import { brandingFeature } from '../../../state/branding.store';
import { isErrorState } from '../../../utilities/state.util';
import { jobToolFeature, selectJob } from '../../job-tool.reducer';
import { commentDefaultToolbar } from '../comments.utils';
import { JobActivityActions } from '../job-activity.actions';
import { EditableComment, PendingFile } from '../job-activity.state';
import { JobCommentArtifactHandlerService } from '../job-comment-artifact-handler.service';
import { AttachmentToolbarQuillModule } from '../quill-modules/attachmentToolbar.quillModule';
import { CameraCaptureQuillModule } from '../quill-modules/cameraCapture.quillModule';
import { ImageUploadQuillModule } from '../quill-modules/imageUpload.quillModule';
import { TimeBlotClickEvent } from '../quill-modules/time.blot';

import { mapCommentEmailStatus } from './mapCommentEmailStatus';
import { mapCommentSmsStatus } from './mapCommentSmsStatus';

export type CommentEditMode = 'view' | 'edit';

export const defaultCommentMode = {
  id: 'Comment',
  name: 'Comment',
  icon: 'pi pi-circle-fill',
};

@Component({
  selector: 'app-job-comment',
  standalone: true,
  imports: [
    CommonModule,
    QuillEditorComponent,
    FormsModule,
    FreyaCommonModule,
    SharedModule,
    QuillTemplateVariableToolbarDirective,
  ],
  templateUrl: './job-comment.component.html',
  styleUrl: './job-comment.component.scss'
})
export class JobCommentComponent implements OnDestroy {

  private _comment$ = new BehaviorSubject<EditableComment | null>(null);
  private subs = new SubSink();
  private replyAttachmentModule: AttachmentToolbarQuillModule | undefined;
  private cameraCaptureModule: CameraCaptureQuillModule | undefined;
  private imageUploadModule: ImageUploadQuillModule | undefined;

  @Input() set comment(value: EditableComment) {
    this._comment$.next(value);
  }

  constructor(
    public store: Store,
    private auth: PlusAuthenticationService,
    private permissions: PermissionService,
    private artifactHandler: JobCommentArtifactHandlerService,
    private artifactService: ArtifactService,
    private notifications: FreyaNotificationsService,
  ) {}

  public viewModules: QuillModules = {
    toolbar: false,
  };

  public handleEditorCreated(quill: Quill) {

    this.attachHandlers(quill);

    this.imageUploadModule?.destroy();
    this.imageUploadModule = quill.getModule('imageUpload') as ImageUploadQuillModule;
    if (this.imageUploadModule) {
      this.imageUploadModule.onImageRejected = (file, reason) => {
        const detail = reason === 'size'
          ? `"${file.name}" exceeds 10 MB limit`
          : `"${file.name}" is not a supported image type`;
        this.notifications.addToast.next({ severity: 'error', summary: 'Image not allowed', detail });
      };

      this.imageUploadModule.onImageSelected = (file, insertImage) => {
        this.uploadAndInsertImage(file, insertImage);
      };
    }

    this.cameraCaptureModule?.destroy();
    this.cameraCaptureModule = quill.getModule('cameraCapture') as CameraCaptureQuillModule;
    if (this.cameraCaptureModule) {
      this.cameraCaptureModule.onImageRejected = (file, reason) => {
        const detail = reason === 'size'
          ? `"${file.name}" exceeds 10 MB limit`
          : `"${file.name}" is not a supported image type`;
        this.notifications.addToast.next({ severity: 'error', summary: 'Image not allowed', detail });
      };

      this.cameraCaptureModule.onImageSelected = (file, insertImage) => {
        this.uploadAndInsertImage(file, insertImage);
      };
    }

    quill.focus();

    // Place cursor at end of line
    quill.setSelection(quill.getLength(), 0);
  }

  public handleReplyEditorCreated(quill: Quill, commentId: string) {
    this.handleEditorCreated(quill);

    this.replyAttachmentModule?.destroy();
    this.replyAttachmentModule = quill.getModule('attachmentToolbar') as AttachmentToolbarQuillModule;
    if (!this.replyAttachmentModule) return;

    this.replyAttachmentModule.onFilesRejected = (rejected) => {
      this.store.dispatch(this.actions.replyFilesRejected({
        rejected: rejected.map((r) => ({ name: r.file.name, reason: r.reason })),
      }));
    };

    this.replyAttachmentModule.onFilesSelected = (files) => {
      const pendingFiles: PendingFile[] = [];
      for (const file of files) {
        const id = generateUUID();
        this.artifactHandler.holdFile(id, file);
        pendingFiles.push({ id, name: file.name, size: file.size, type: file.type });
      }
      this.store.dispatch(this.actions.replyFilesSelected({ commentId, files: pendingFiles }));
    };
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.replyAttachmentModule?.destroy();
    this.cameraCaptureModule?.destroy();
    this.imageUploadModule?.destroy();
  }

  private uploadAndInsertImage(file: File, insertImage: (url: string) => void): void {
    this.subs.sink = this.artifactService.createArtifact([{
      name: file.name,
      contentType: file.type,
      fileSize: file.size,
      namespace: 'job-comment-inline-image',
      public: true,
    }]).pipe(
      switchMap((result) => {
        const artifact = result.data.createArtifacts.artifacts[0];
        const policy = JSON.parse(artifact.signedPolicy);
        return this.artifactService.uploadFile(file as any, policy).pipe(
          map(() => artifact.url),
        );
      }),
    ).subscribe({
      next: (url) => insertImage(url),
      error: () => {
        this.notifications.addToast.next({
          severity: 'error',
          summary: 'Image upload failed',
          detail: `Could not upload "${file.name}"`,
        });
      },
    });
  }

  public attachHandlers(quill: Quill) {
    quill.on(
      'time-click',
      (event: TimeBlotClickEvent) => this.store.dispatch(this.actions.quillEditorTimeClicked({ time: event.formattedTime })),
    );
  }

  public actions = JobActivityActions;

  public variablesLocation = QuillTemplateLocation.GenericJobComment;

  public viewModel$ = combineLatest(
    this._comment$,
    this.store.select(createSelector({
      job: selectJob,
      addReplyContents: jobToolFeature.selectAddReplyContents,
      addReplyPendingFiles: jobToolFeature.selectAddReplyPendingFiles,
      editCommentContents: jobToolFeature.selectEditCommentContents,
      repliesCollapsed: jobToolFeature.selectRepliesCollapsed,
      mutating: jobToolFeature.selectCommentsMutating,
      commentModes: brandingFeature.selectCommentModes,
    })),
    this.permissions.watchPermissions([ 'comments.delete', 'comments.update' ]),
    this.permissions.watchPermissionsAndRestrictions([
      {
        permission: 'comments.delete',
        restriction: { overrideOwnership: true },
      },
      {
        permission: 'comments.update',
        restriction: { overrideOwnership: true },
      },
    ]),
  ).pipe(
    map(([
      comment,
      { job, addReplyContents, addReplyPendingFiles, editCommentContents, repliesCollapsed, mutating, commentModes },
      [ canDeleteOwn, canEditOwn ],
      [ canDeleteAny, canEditAny ]
    ]) => {

      const isOwn = comment?.author?.id === this.auth.user?.id;

      const canDelete = (canDeleteOwn && isOwn) || canDeleteAny;

      const replyContents = addReplyContents[comment.id];

      const editContents = editCommentContents[comment.id];

      const commentMode = this._getCommentMode(comment, commentModes);

      const editCommentHandler = () => this.store.dispatch(this.actions.commentEditEnterKeyPressed({ commentId: comment.id }));

      const addReplyHandler = () => this.store.dispatch(this.actions.replyInputEnterKeyPressed({ commentId: comment.id }));

      return {
        mutating,
        commentModes,
        comment,
        contents: comment.contents,
        canDelete,
        replyContents,
        commentMode,
        templatesLocations: [COMMENT_MODES_TEMPLATES[commentMode.id]],
        canEdit: (canEditOwn && isOwn) || canEditAny,
        showMenu: !canDelete,
        isEditing: Boolean(editContents),
        replyOpened: Boolean(replyContents),
        replyEmpty: !replyContents?.ops?.length,
        isRepliesExpanded: !(comment.id in repliesCollapsed),
        commentEditContents: editContents,
        authorJobRoles: this._findAuthorJobRoles(comment?.author, job),
        emailStatus: mapCommentEmailStatus(comment),
        smsStatus: mapCommentSmsStatus(comment),
        systemContents: this._getSystemContents(comment),
        replies: comment && 'replies' in comment ? comment.replies : [],
        hasError: isErrorState(comment?.callState),
        replyPendingFiles: addReplyPendingFiles[comment.id] || [],
        editCommentModules: this._getQuillModules(editCommentHandler),
        addReplyModules: this._getQuillModules(addReplyHandler, true),
        commentMenuItems: this._getMenuItems(comment),
      };
    }),
  );

  private _getCommentMode(
    comment: EditableComment,
    commentModes: BaseCommentModeFragment[]
  ) {
    if (!commentModes?.length) { return defaultCommentMode; }
    const commentMode = commentModes.find((m) => m.id === comment.modeId);
    if (!commentMode) { return defaultCommentMode; }
    return commentMode;
  }

  private _findAuthorJobRoles(
    author: BaseUserFragment,
    job: FullJobFragment,
  ): BaseJobUserFragment[] {

    if (!job?.users?.length) { return []; }

    if (!author) { return []; }

    return job.users
      .filter((u) => u.user?.id === author.id)
      .sort((a, b) => a.role > b.role ? -1 : 1);
  }

  private _getSystemContents(comment: EditableComment) {

    let systemContents = new Delta();

    // SET AUTHOR
    let authorName = comment?.authorName;

    const author = comment?.author;

    if (author) {
      authorName = [ author.givenName, author.familyName ]
        .filter(Boolean)
        .join(' ');

      if (comment?.authorName
        && authorName !== comment?.authorName
        && comment?.authorName !== 'System'
      ) {
        authorName += ` (${ comment.authorName })`;
      }
    }

    if (authorName) {
      authorName = authorName.trim();
    }

    systemContents.insert(
      authorName || 'System',
      {
        details: author && {
          type: 'users',
          item: author,
        },
      },
    );

    systemContents.insert(' ');

    // SET CONTENTS
    systemContents = systemContents.concat(comment.contents);
    systemContents.insert(' ');

    // SET TIME
    const appropriateTime = appropriateTimeMeasurement(comment?.createdAt);

    systemContents.insert(appropriateTime, {
      time: comment?.createdAt,
    });

    return systemContents;
  }

  private _getQuillModules(enterHandler: () => void, attachmentToolbar = false): QuillModules {
    return {
      toolbar: commentDefaultToolbar,
      variableToolbar: true,
      templateToolbar: true,
      attachmentToolbar,
      imageUpload: true,
      cameraCapture: true,
      keyboard: {
        bindings: {
          submit: {
            key: 'Enter',
            ctrlKey: true,
            handler: enterHandler,
          },
        },
      },
    }
  }

  private _getMenuItems(comment: EditableComment): MenuItem[] {
    return [
      {
        label: 'Delete Comment',
        styleClass: 'delete-comment',
        command: () => {
          this.store.dispatch(this.actions.deleteCommentMenuItemClicked({ commentId: comment.id }));
        }
      },
    ];
  }

  identifyReply(_index: number, reply: EditableComment) {
    return reply.id;
  }

}
